import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { Observable, Subject, merge, of } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { NotificationService } from '../../../services/notification.service';
import { FamilyPlanSettingsService } from '../../../services/family-plan-settings.service';
import type {
  FamilyDiscountType,
  FamilyPlanSettingsData,
  FamilyPlanSettingsLimits,
} from '../../types/family-plan-settings.types';
import {
  DEFAULT_FAMILY_LIMITS,
  FamilyChip,
  FamilyPlanDraft,
  addFamilyCustomValue,
  buildFamilyPayload,
  emptyFamilyDraft,
  familyChips,
  familyVehicleOptions,
  formatFamilyValue,
  isFamilyDraftDirty,
  removeFamilyValue,
  setFamilyValues,
  setFamilyDiscountType,
  toFamilyDraft,
  validateFamilyDraft,
} from '../../utils/family-plan-settings.util';

/**
 * Family Plan section of Configure Sales Panel (CWS-1582). Scoped to the modal's selected
 * location; the host saves it from the modal's Apply button via `save()`.
 */
@Component({
  selector: 'app-family-plan-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatChipsModule, MatSelectModule],
  providers: [NotificationService],
  templateUrl: './family-plan-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./family-plan-settings.component.scss'],
})
export class FamilyPlanSettingsComponent implements OnChanges, OnDestroy {
  private readonly settingsService = inject(FamilyPlanSettingsService);
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  /** Fires on every (re)load so a slow response for a previous location is dropped. */
  private readonly reload$ = new Subject<void>();

  @Input() isActive = false;
  @Input() locationId: string | null = null;
  /** Lets the host hold its own reveal until this section has loaded (CWS-598). */
  @Output() loadingChange = new EventEmitter<boolean>();
  /** True when there are unsaved, editable changes. */
  @Output() dirtyChange = new EventEmitter<boolean>();

  readonly discountTypes: { value: FamilyDiscountType; label: string; hint: string }[] = [
    {
      value: 'percentage',
      label: 'Percentage off',
      hint: "A % off each additional vehicle's List Price/mo.",
    },
    {
      value: 'flat',
      label: 'Flat amount off',
      hint: 'A fixed $ off each additional vehicle, every month.',
    },
  ];

  /** False when the family_plan flag is off for the location: the section renders nothing. */
  enabled = false;
  isLoading = false;
  isSaving = false;
  limits: FamilyPlanSettingsLimits = DEFAULT_FAMILY_LIMITS;
  draft: FamilyPlanDraft = emptyFamilyDraft();
  customInput = '';
  addError = '';
  formError = '';

  private saved: FamilyPlanDraft = emptyFamilyDraft();
  private reported = false;
  /** Only the first load of an open cycle holds the host's reveal; a location switch reloads quietly. */
  private reportInitialLoad = false;

  get vehicleOptions(): number[] {
    return familyVehicleOptions(this.limits);
  }

  get chips(): FamilyChip[] {
    return familyChips(this.draft);
  }

  get isDirty(): boolean {
    return this.enabled && isFamilyDraftDirty(this.draft, this.saved);
  }

  /** At the preset cap: unselected chips are disabled so the draft can't exceed what the API accepts. */
  get isAtPresetLimit(): boolean {
    return this.draft.values.length >= this.limits.max_presets;
  }

  get isInteractionDisabled(): boolean {
    return this.isLoading || this.isSaving;
  }

  get canAddCustom(): boolean {
    return (
      !this.isInteractionDisabled && this.customInput.trim() !== '' && !!this.draft.discountType
    );
  }

  get customAffix(): string {
    return this.draft.discountType === 'flat' ? '$' : '%';
  }

  get valueHint(): string {
    return this.draft.discountType === 'flat'
      ? "Taken off each additional vehicle's List Price/mo, regardless of package price."
      : "Shown as options when vehicles are added to an order. Applied to each additional vehicle's List Price/mo.";
  }

  ngOnChanges(changes: SimpleChanges): void {
    const becameActive = changes['isActive']?.currentValue === true;
    const locationChanged = !!changes['locationId'] && this.isActive;
    if (becameActive) {
      this.reportInitialLoad = true;
    }
    if (becameActive || locationChanged) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  label(value: number): string {
    return formatFamilyValue(this.draft.discountType, value);
  }

  onMaxVehiclesChange(value: number | null): void {
    this.draft = { ...this.draft, maxVehicles: value };
    this.touched();
  }

  onDiscountTypeChange(type: FamilyDiscountType): void {
    if (this.isInteractionDisabled) {
      return;
    }
    this.draft = setFamilyDiscountType(this.draft, type);
    this.addError = '';
    this.customInput = '';
    this.touched();
  }

  trackChip(_index: number, chip: FamilyChip): number {
    return chip.value;
  }

  /** The listbox emits only for the user's own toggles, with every selected value. */
  onChipsChange(values: number[] | null): void {
    if (this.isInteractionDisabled) {
      return;
    }
    this.draft = setFamilyValues(this.draft, values ?? []);
    this.touched();
  }

  onChipRemove(chip: FamilyChip): void {
    if (this.isInteractionDisabled) {
      return;
    }
    this.draft = removeFamilyValue(this.draft, chip.value);
    this.touched();
  }

  onAddCustom(): void {
    if (!this.canAddCustom) {
      return;
    }
    const result = addFamilyCustomValue(this.draft, this.customInput, this.limits);
    this.addError = result.error ?? '';
    if (!result.error) {
      this.draft = result.draft;
      this.customInput = '';
      this.touched();
    }
  }

  onCustomInputChange(): void {
    this.addError = '';
  }

  onDefaultChange(value: number | null): void {
    this.draft = { ...this.draft, defaultValue: value };
    this.touched();
  }

  onCustomEnabledChange(checked: boolean): void {
    this.draft = { ...this.draft, customEnabled: checked };
    this.touched();
  }

  /** Validates and saves. Emits `true` when saved or when there is nothing to save. */
  save(): Observable<boolean> {
    if (!this.enabled || !this.locationId) {
      return of(true);
    }
    const error = validateFamilyDraft(this.draft, this.limits);
    if (error) {
      this.formError = error;
      return of(false);
    }
    this.formError = '';
    this.isSaving = true;
    return this.settingsService.saveSettings(buildFamilyPayload(this.locationId, this.draft)).pipe(
      takeUntil(this.destroy$),
      tap(res => {
        this.isSaving = false;
        if (res.status === false) {
          throw new Error(res.message || 'The server did not save family plan settings.');
        }
        this.applyData(res.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: res.message || 'Family plan settings saved successfully.',
        });
      }),
      map(() => true),
      catchError(err => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Save failed',
          detail: err?.error?.message || err?.message || 'Could not save family plan settings.',
        });
        return of(false);
      })
    );
  }

  private touched(): void {
    this.formError = '';
    this.emitDirty();
  }

  private load(): void {
    this.reload$.next();
    if (!this.locationId) {
      this.enabled = false;
      this.finishInitialLoad();
      return;
    }
    this.setLoading(true);
    this.settingsService
      .getSettings(this.locationId)
      .pipe(
        takeUntil(merge(this.destroy$, this.reload$)),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: err?.error?.message || 'Could not load family plan settings.',
          });
          return of(null);
        })
      )
      .subscribe(res => {
        if (res && res.status !== false && res.data) {
          this.applyData(res.data);
        } else {
          this.enabled = false;
        }
        this.setLoading(false);
        this.finishInitialLoad();
      });
  }

  private applyData(data: FamilyPlanSettingsData): void {
    this.enabled = data.enabled;
    this.limits = data.limits ?? DEFAULT_FAMILY_LIMITS;
    this.saved = toFamilyDraft(data.settings);
    this.draft = { ...this.saved, values: [...this.saved.values] };
    this.customInput = '';
    this.addError = '';
    this.formError = '';
    this.emitDirty();
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  /** Tells the host this section is ready (once per open cycle), including when there is nothing to load. */
  private finishInitialLoad(): void {
    if (this.reportInitialLoad) {
      this.reportInitialLoad = false;
      this.loadingChange.emit(false);
    }
  }

  private emitDirty(): void {
    const dirty = this.isDirty;
    if (dirty !== this.reported) {
      this.reported = dirty;
      this.dirtyChange.emit(dirty);
    }
  }
}
